import { axiosInstance } from 'utils';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import useError from 'hooks/useError';

type Props = {
  children: ReactNode;
};

export type ClientOffer = {
  term: number;
  firstPaymentDate?: string;
  startDate?: string;
  monthlyPayment: number;
  totalPrincipal: number;
  totalInterest: number;
  totalRepayableAmount: number;
  agreementEndDate: string;
  newPrincipal: number;
  newAmountWithInterestAndInitialCommission: number;
  newPrincipalWithAllCapitalizableFees: number;
  newAmountInterest: number; // Used for additional amount
  newInterest: number; // Used for additional amount
  newInterestBeforeDiscount: number;
  discountPercent: number;
  discounts: Array<{
    expirationDate: string;
  }>;
  preliminarySchedule?: Array<{
    commissionAmount: number;
    discountAmount: number;
    dueDate: string;
    initialCommissionAmount: number;
    initialCommissionDiscountAmount: number;
    interestAmount: number;
    interestDiscountAmount: number;
    invoicedCommissionAmount: number;
    invoicedInitialCommissionAmount: number;
    invoicedInterestAmount: number;
    invoicedPrincipalAmount: number;
    number: number;
    penaltyAmount: number;
    principalAmount: number;
    remainingPrincipal: number;
    totalAmount: number;
    totalDiscountAmount: number;
  }>;
};

type ClientOfferContextType = {
  clientOffers?: ClientOffer[];
  fetchClientOffer: (
    productNumber: string | undefined,
    amount: number,
    term: number,
  ) => Promise<ClientOffer | void>;
  fetchMultipleTermClientOffers: (
    productNumber: string,
    amount: number,
    terms: number[],
  ) => Promise<ClientOffer[] | void>;
  setClientOffers: React.Dispatch<
    React.SetStateAction<ClientOffer[] | undefined>
  >;
};

const ClientOfferContext = createContext<ClientOfferContextType>(
  {} as ClientOfferContextType,
);

const useClientOffer = (): ClientOfferContextType =>
  useContext(ClientOfferContext);

const ClientOfferProvider = ({ children }: Props): JSX.Element => {
  const [clientOffers, setClientOffers] = useState<ClientOffer[]>();
  const { showError } = useError();

  const fetchClientOffer = useCallback(
    async (
      productNumber: string | undefined,
      amount: number,
      term: number,
    ): Promise<ClientOffer | void> => {
      try {
        const { data: fetchedClientOffer } = await axiosInstance.get(
          productNumber
            ? `/client/application/offer/product-number/${productNumber}?amount=${amount}&term=${term}`
            : `/client/application/offer?amount=${amount}&term=${term}`,
          {
            headers: {
              Accept: 'application/json',
            },
          },
        );

        setClientOffers([fetchedClientOffer]);

        return fetchedClientOffer;
      } catch (e) {
        showError();
        throw e;
      }
    },
    [showError],
  );

  /** Fetch offers for multiple terms. Handy for VIVUS_IL (instalment) calculator */
  const fetchMultipleTermClientOffers = useCallback(
    async (productNumber: string, amount: number, terms: number[]) => {
      try {
        const clientOffers = await Promise.all(
          terms.map(async (term) => {
            const response = await axiosInstance.get(
              `/client/application/offer/product-number/${productNumber}?amount=${amount}&term=${term}`,
            );
            return response.data;
          }),
        );

        setClientOffers(clientOffers);

        return clientOffers;
      } catch (e) {
        showError();
        throw e;
      }
    },
    [showError],
  );

  const clientOfferContextValue = useMemo(
    () => ({
      clientOffers,
      fetchClientOffer,
      fetchMultipleTermClientOffers,
      setClientOffers,
    }),
    [
      clientOffers,
      fetchClientOffer,
      fetchMultipleTermClientOffers,
      setClientOffers,
    ],
  );

  return (
    <ClientOfferContext.Provider value={clientOfferContextValue}>
      {children}
    </ClientOfferContext.Provider>
  );
};

export { useClientOffer as default, ClientOfferProvider };
